import React, { useState } from 'react';
import Link from 'next/link';
import { pdfjs } from 'react-pdf';
import { toast } from 'react-toastify';
import { IcertificateDetails } from '../../TypesAndInterfaces';
import { getCertificatePdfUrl } from '../utils/certificateUtils';
import {
  sendDownloadCtaClickedEvent,
  sendCertificateDownloadEvent,
} from '../utils/certificateGAEvents';
import DownloadModal from './DownloadModal';

type DownloadType = 'pdf' | 'image';

interface DownloadButtonsProps {
  certificateDetails: IcertificateDetails;
  allCertificates: IcertificateDetails[];
  isCareerPath: boolean;
  selectedIndex: number;
}

const DownloadButtons: React.FC<DownloadButtonsProps> = ({
  certificateDetails,
  allCertificates,
  isCareerPath,
  selectedIndex,
}) => {
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadType, setDownloadType] = useState<DownloadType>('pdf');
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);

  const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;

  const handleOpenDownloadModal = (type: DownloadType) => {
    // Track download CTA click when opening modal for multiple certificates
    sendDownloadCtaClickedEvent({
      click_text: `Download as ${type === 'pdf' ? 'PDF' : 'Image'}`,
      download_type: type,
      certificates_count: allCertificates.length,
      certificate_ids: allCertificates.map((c) => c.certificate?.id).filter(Boolean),
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });
    setDownloadType(type);
    setShowDownloadModal(true);
  };

  // Track single PDF download click
  // Note: Cannot track download completion for browser-native link downloads
  const handleSinglePdfClick = () => {
    sendDownloadCtaClickedEvent({
      click_text: 'Download as PDF',
      download_type: 'pdf',
      certificates_count: 1,
      certificate_ids: [certificateDetails?.certificate?.id].filter(Boolean),
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });
  };

  // Download single certificate as image
  const handleDownloadSingleImage = async () => {
    // Track download CTA click
    sendDownloadCtaClickedEvent({
      click_text: 'Download as Image',
      download_type: 'image',
      certificates_count: 1,
      certificate_ids: [certificateDetails?.certificate?.id].filter(Boolean),
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });

    const pdfUrl = getCertificatePdfUrl(certificateDetails?.certificate?.id, isCareerPath);
    setIsDownloadingImage(true);
    try {
      const loadingTask = pdfjs.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const scale = 1;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext: any = {
        canvasContext: context,
        viewport,
      };
      await page.render(renderContext).promise;
      const downloadImage = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = downloadImage;
      link.download = `${certificateDetails?.certificate?.id}.png`;
      link.click();

      // Track successful download
      sendCertificateDownloadEvent({
        download_type: 'image',
        certificates_count: 1,
        certificate_ids: [certificateDetails?.certificate?.id].filter(Boolean),
        course_name: certificateDetails?.courseName,
        course_id: certificateDetails?.certificate?._id,
        partner_certs_available_count: partnerCertCount,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to download certificate as image:', err);
      toast.error('Failed to download certificate. Please try again.');
    } finally {
      setIsDownloadingImage(false);
    }
  };

  const singlePdfUrl = getCertificatePdfUrl(certificateDetails?.certificate?.id, isCareerPath);
  const hasMultipleCertificates = allCertificates.length > 1;

  return (
    <>
      <div className="mt-[20px] flex flex-col gap-4">
        {hasMultipleCertificates ? (
          <button
            type="button"
            onClick={() => handleOpenDownloadModal('pdf')}
            className="bg-[#E97862] text-white px-[24px] py-[10px] rounded-[6px] flex items-center justify-center"
          >
            Download as PDF
          </button>
        ) : (
          <Link href={singlePdfUrl} passHref legacyBehavior>
            <a
              id="download-pdf"
              href="/!#"
              target="_blank"
              download
              rel="noreferrer nofollow"
              onClick={handleSinglePdfClick}
              className="bg-[#E97862] text-white px-[24px] py-[10px] rounded-[6px] flex items-center justify-center"
            >
              Download as PDF
            </a>
          </Link>
        )}
        {hasMultipleCertificates ? (
          <button
            type="button"
            onClick={() => handleOpenDownloadModal('image')}
            className="text-[#E97862] bg-white px-[24px] py-[10px] rounded-[6px] flex items-center justify-center border border-[#E97862]"
          >
            Download as Image
          </button>
        ) : (
          <button
            type="button"
            onClick={handleDownloadSingleImage}
            disabled={isDownloadingImage}
            className="text-[#E97862] bg-white px-[24px] py-[10px] rounded-[6px] flex items-center justify-center border border-[#E97862]"
          >
            {isDownloadingImage ? 'Downloading...' : 'Download as Image'}
          </button>
        )}
      </div>

      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        allCertificates={allCertificates}
        selectedIndex={selectedIndex}
        isCareerPath={isCareerPath}
        downloadType={downloadType}
      />
    </>
  );
};

export default DownloadButtons;
